import { Controller } from "@hotwired/stimulus";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  RecaptchaVerifier,
  signInWithPhoneNumber,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: "AIzaSyAA4GharXISHrFFWOfNsnpHGUvSGxkVJjc",
  authDomain: "ezysolz.firebaseapp.com",
  projectId: "ezysolz",
  storageBucket: "ezysolz.firebasestorage.app",
  messagingSenderId: "56621940220",
  appId: "1:56621940220:web:a04cf4fe42dbff58434679",
  measurementId: "G-D92L6TWGVX",
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
auth.useDeviceLanguage();

export default class extends Controller {
  static targets = [
    "phoneInput",
    "otpInput",
    "step1",
    "step2",
    "message",
    "countrySelect",
    "resendButton",
    // New Animation Targets
    "sendCodeButton",
    "sendCodeSpinner",
    "sendCodeText",
    "verifyCodeButton",
    "verifyCodeSpinner",
    "verifyCodeText",
  ];

  connect() {
    this.setupRecaptcha();
    this.timer = null;
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer);
  }

  setupRecaptcha() {
    // 1. Change "sign-in-button" to "recaptcha-container"
    // 2. Delete the callback function entirely. Firebase will handle it automatically!
    window.recaptchaVerifier = new RecaptchaVerifier(
      auth,
      "recaptcha-container",
      {
        size: "invisible",
        "expired-callback": () => {
          this.showMessage(
            "reCAPTCHA expired. Please try again.",
            "text-[#BA1A1A]",
          );
        },
      },
    );
  }

  // Helper method for handling UI loading states
  toggleLoading(button, spinner, textElement, isLoading, loadingText = "") {
    if (isLoading) {
      button.disabled = true;
      spinner.classList.remove("hidden");
      textElement.dataset.originalText = textElement.textContent;
      if (loadingText) textElement.textContent = loadingText;
    } else {
      button.disabled = false;
      spinner.classList.add("hidden");
      if (textElement.dataset.originalText) {
        textElement.textContent = textElement.dataset.originalText;
      }
    }
  }

  async sendCode(event) {
    if (event) event.preventDefault();

    const countryCode = this.countrySelectTarget.value;
    let localNumber = this.phoneInputTarget.value
      .trim()
      .replace(/[\s\-()]/g, "");

    if (!localNumber) {
      this.showMessage("Please enter your mobile number.", "text-[#BA1A1A]");
      return;
    }

    if (localNumber.startsWith("0")) {
      localNumber = localNumber.substring(1);
    }

    const fullPhoneNumber = `${countryCode}${localNumber}`;
    const appVerifier = window.recaptchaVerifier;

    try {
      this.showMessage("Sending code...", "text-[#006A6A]");

      // Start button animation
      this.toggleLoading(
        this.sendCodeButtonTarget,
        this.sendCodeSpinnerTarget,
        this.sendCodeTextTarget,
        true,
        "Sending SMS...",
      );

      this.confirmationResult = await signInWithPhoneNumber(
        auth,
        fullPhoneNumber,
        appVerifier,
      );

      // Stop button animation on success
      this.toggleLoading(
        this.sendCodeButtonTarget,
        this.sendCodeSpinnerTarget,
        this.sendCodeTextTarget,
        false,
      );

      this.step1Target.classList.add("hidden");
      this.step2Target.classList.remove("hidden");
      this.showMessage("Code sent successfully!", "text-[#006A6A]");

      this.startResendCooldown();
    } catch (error) {
      // Stop button animation on error
      this.toggleLoading(
        this.sendCodeButtonTarget,
        this.sendCodeSpinnerTarget,
        this.sendCodeTextTarget,
        false,
      );

      console.error("SMS Delivery Error:", error);
      this.showMessage(
        `Failed to send SMS: ${error.message}`,
        "text-[#BA1A1A]",
      );

      window.recaptchaVerifier.render().then((widgetId) => {
        if (window.grecaptcha) {
          window.grecaptcha.reset(widgetId);
        }
      });
    }
  }

  async resendCode(event) {
    if (event) event.preventDefault();
    if (this.resendButtonTarget.disabled) return;
    this.otpInputTarget.value = "";
    await this.sendCode();
  }

  startResendCooldown() {
    if (this.timer) clearInterval(this.timer);

    let secondsLeft = 60;
    this.resendButtonTarget.disabled = true;
    this.resendButtonTarget.textContent = `Resend OTP (${secondsLeft}s)`;

    this.timer = setInterval(() => {
      secondsLeft--;
      this.resendButtonTarget.textContent = `Resend OTP (${secondsLeft}s)`;

      if (secondsLeft <= 0) {
        clearInterval(this.timer);
        this.resendButtonTarget.disabled = false;
        this.resendButtonTarget.textContent = "Resend OTP";
      }
    }, 1000);
  }

  async verifyCode(event) {
    event.preventDefault();
    const code = this.otpInputTarget.value.trim();

    if (code.length !== 6) {
      this.showMessage(
        "Please enter a complete 6-digit code.",
        "text-[#BA1A1A]",
      );
      return;
    }

    try {
      this.showMessage("Verifying code...", "text-[#006A6A]");

      // Start button animation
      this.toggleLoading(
        this.verifyCodeButtonTarget,
        this.verifyCodeSpinnerTarget,
        this.verifyCodeTextTarget,
        true,
        "Verifying...",
      );

      const result = await this.confirmationResult.confirm(code);
      const idToken = await result.user.getIdToken();

      this.createRailsSession(idToken);
    } catch (error) {
      // Stop button animation on error
      this.toggleLoading(
        this.verifyCodeButtonTarget,
        this.verifyCodeSpinnerTarget,
        this.verifyCodeTextTarget,
        false,
      );

      console.error("Verification Match Error:", error);
      this.showMessage(
        "Invalid verification code. Try again.",
        "text-[#BA1A1A]",
      );
    }
  }

  async createRailsSession(idToken) {
    const csrfToken = document.querySelector("[name='csrf-token']").content;

    try {
      const response = await fetch("/session", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({ id_token: idToken }),
      });

      if (response.ok) {
        if (this.timer) clearInterval(this.timer);
        // Do not toggle loading state back to false here, keep it spinning while the page redirects
        window.location.href = "/";
      } else {
        // Turn off spinner if Rails rejects the payload
        this.toggleLoading(
          this.verifyCodeButtonTarget,
          this.verifyCodeSpinnerTarget,
          this.verifyCodeTextTarget,
          false,
        );

        const errorData = await response.json().catch(() => ({}));
        this.showMessage(
          errorData.error || "Server verification failed",
          "text-[#BA1A1A]",
        );
      }
    } catch (serverError) {
      // Turn off spinner if the network fails completely
      this.toggleLoading(
        this.verifyCodeButtonTarget,
        this.verifyCodeSpinnerTarget,
        this.verifyCodeTextTarget,
        false,
      );

      console.error("Rails Network Communication Failure:", serverError);
      this.showMessage(
        "Network error occurred during server validation.",
        "text-[#BA1A1A]",
      );
    }
  }

  showMessage(msg, colorClass) {
    this.messageTarget.textContent = msg;
    this.messageTarget.className = `text-xs font-semibold tracking-wide uppercase ${colorClass}`;
  }
}
